// Full code with 10 unique frames per animation list
import React, { useState, useEffect, useRef } from "react";
import "./Tamagotchi.css";

export default function Tamagotchi() {
  const [hunger, setHunger] = useState(5);
  const [happiness, setHappiness] = useState(5);
  const [health, setHealth] = useState(10);
  const [age, setAge] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [sleeping, setSleeping] = useState(false);
  const [alive, setAlive] = useState(true);
  const [frameIndex, setFrameIndex] = useState(0);
  const [animFrame, setAnimFrame] = useState(null);
  const [message, setMessage] = useState("Your cat is born!");
  const [attention, setAttention] = useState(false);
  const [menuIndex, setMenuIndex] = useState(0);

  const poopTimer = useRef(0);


  /*
  LINKS:
    Server Not Found: https://www.asciiart.eu/animals/cats
    ascii-art.de/ascii/c/cat.txt: http://www.ascii-art.de/ascii/c/cat.txt
    CATS - ASCII ART: https://ascii.co.uk/art/cats
  */

  // Idle animation: cat looks around, lifts a paw, and twitches its tail.
  // Play animation: cat crouches, leaps, and bats at a ball of yarn.
  // Eat animation: cat leans down to its bowl.
  // Sleep animation: cat is curled up.
  // Dirty state: cat sits unhappily next to a poop.
  // Dead state: a ghostly cat with X's for eyes.

  const idleFrames = [
    `
           |\\      _,,,---,,_
           /, \`.-'\`'    -.  ;-;;,_
          |,4-  ) )-,_..;\\ (  \`'-'
         '---''(_/--'  \`-'\\_)`,
    `
           |\\      _,,,---,,_
           /, \`.-'\`'    -.  ;-;;,_
          |,4-  ) )-,_..;\\ (  \`'-'
          '-- (_/--'  \`-'\\_)`,
    `
           |\\      _,,,---,,_
           /, \`.-'\`'    -.  ;-;;,_
          |,4-  ) )-,_..;\\ (  \`'-'
         '---''(_/--'  \`-'\\_))`,
    `
             /\\      _,,,---,,_
            / , \`.-'\`'    -.  ;-;;,_
           /,'-  ) )-,_..;\\ (  \`'-'
          '---''(_/--'  \`-'\\_)`,
  ];

  // Play animation: cat crouches, leaps, and bats at a ball of yarn.
  const playFrames = [
    `
        
                     ,---,,_
               |\\   .'\`' o o ;-;;,_
               \\,4- ) )-,_..;\\ (  \`'-'
                '--' (_/--'  \`-'\\_) &`,
    `
        
              _,,,---,,_
              \`' o o ;-;;,_
              ( )-,_..;\\ (  \`'-'
              (_/--'  \`-'\\_)   &`,
    `
               |\\      _,,,---,,_
               /, \`.-'\`'  ^ ^ ;-;;,_
              (4-  ) )-,_..;\\ (  \`'-'
              '---''(_/--'  \`-'\\_)&`,
  ];

  // Eat animation: cat leans down to its bowl.
  const eatFrames = [
    `
               |\\      _,,,---,,_
               /, \`.-'\`'    -.  ;-;;,_
              |,4-  ) )-,_..;\\ (  \`'-'
             '---''(_/--'  \`-'\\_)`,
    `
               |\\      _,,,---,,_
               /, \`.-'\`'    -.  ;-;;,_
              |,4-  ) )-,_..;\\ (  \`'-'
             '---''(_/--'  \`-'\\_)[ ]`,
    `
               |\\      _,,,---,,_
               /, \`.-''-,,  ..;;,_
              |,4-  ) )-' ''-'(  \`'-'
             '---''(_/--'    \`-'\\_U]`,
  ];

  // Sleep animation: cat is curled up.
  const sleepFrames = [
    `
               |\\      _,,,---,,_
          zZZ /, \`.-'\`'    -.  ;-;;,_
             |,4-  ) )-,_..;\\ (  \`'-'
            '---''(_/--'  \`-'\\_)`,
    `
               |\\      _,,,---,,_
          ZzZ /, \`.-'\`'    -.  ;-;;,_
             |,4-  ) )-,_..;\\ (  \`'-'
            '---''(_/--'  \`-'\\_)`,
    `
               |\\      _,,,---,,_
           ZZz /, \`.-'\`'    -.  ;-;;,_
              |,4-  ) )-,_..;\\ (  \`'-'
             '---''(_/--'  \`-'\\_)`,
  ];
  // Dirty state: cat sits unhappily next to a poop.
  const dirtyFrame = `
           |\\      _,,,---,,_
           /, \`.-'\`'  \`_  -.  ;-;;,_
          |,_  ) )-,_..;\\ (  \`'-'
         '---''(_/--'  \`-'\\_)  @`;

  // Dead state: a ghostly cat with X's for eyes.
  const deadFrame = `
           |\\      _,,,---,,_
           /, \`.-'\`'    -.  ;-;;,_
          |,_x-  ) )-,_..;\\ (  \`'-'
         '---''(_/--'  \`-'\\_)
            R. I. P.`;
  const menuOptions = ["Feed", "Play", "Clean", "Med", "Sleep", "Disc"];

  // --- Game Loop: Manages stat decay and state changes ---
  useEffect(() => {
    if (!alive) return;

    const interval = setInterval(() => {
      // Pause logic while sleeping or during a temporary animation
      if (!sleeping && !animFrame) {
        // Only update stats and timers, don't move the cat (let frames handle animation)
        setHunger((h) => Math.max(0, h - 0.02));
        setHappiness((hp) => Math.max(0, hp - 0.01));
        poopTimer.current += 1;

        // Cat poops after a certain amount of time
        if (poopTimer.current > 60) {
          setDirty(true);
          poopTimer.current = 0;
          setMessage("Uh oh, smelly!");
        }

        // Check if the cat needs attention
        if (hunger < 3 || happiness < 3 || dirty) {
          setAttention(true);
        } else {
          setAttention(false);
        }
      } else if (sleeping) {
        // Only update stats, don't move the cat (let frames handle animation)
        setHappiness((hp) => Math.min(10, hp + 0.01));
        setHealth((hp) => Math.min(10, hp + 0.01));
      }

      setAge((a) => a + 0.001);
    }, 700);

    return () => clearInterval(interval);
  }, [alive, sleeping, animFrame, hunger, happiness, dirty]);

  // --- Frame Animation Loop ---
  useEffect(() => {
    if (!alive || animFrame) return;

    let frameTimer;
    if (sleeping) {
      frameTimer = setInterval(() => {
        setFrameIndex((i) => (i + 1) % sleepFrames.length);
      }, 700);
    } else if (!dirty) {
      frameTimer = setInterval(() => {
        setFrameIndex((i) => (i + 1) % idleFrames.length);
      }, 700);
    }
    // If dirty, don't animate (static dirtyFrame)
    return () => clearInterval(frameTimer);
  }, [alive, sleeping, animFrame, dirty]);

  // --- Death Check ---
  useEffect(() => {
    if (hunger <= 0 || health <= 0 || age > 15) {
      setAlive(false);
      setMessage("Your cat has crossed the rainbow bridge...");
    }
  }, [hunger, health, age]);

  // --- Generic Animation Player ---
  const animate = (frames) => {
    let i = 0;
    const step = () => {
      setAnimFrame(frames[i % frames.length]);
      i++;
      if (i < frames.length) {
        setTimeout(step, 400);
      } else {
        // Clear the animation frame after it's done
        setTimeout(() => setAnimFrame(null), 500);
      }
    };
    step();
  };

  // --- Action Handlers ---
  const actions = {
    Feed: () => {
      setHunger((h) => Math.min(10, h + 2));
      animate(eatFrames);
    },
    Play: () => {
      setHappiness((hp) => Math.min(10, hp + 2));
      animate(playFrames);
    },
    Clean: () => {
      setDirty(false);
      poopTimer.current = 0;
    },
    Med: () => setHealth((hp) => Math.min(10, hp + 2)),
    Sleep: () => setSleeping((s) => !s),
    Disc: () => setAttention(false),
  };

  const handleConfirm = () => {
    if (!alive) return;
    const currentAction = menuOptions[menuIndex];

    // Special handling for sleep to allow waking up
    if (currentAction === "Sleep") {
      actions.Sleep();
      setMessage(sleeping ? "Woke up!" : "Good night!");
      setTimeout(() => setMessage(""), 1500);
      return;
    }

    // Prevent other actions while sleeping
    if (sleeping) {
      setMessage("Shhh, the cat is sleeping...");
      setTimeout(() => setMessage(""), 1500);
      return;
    }

    // Prevent actions while another animation is playing
    if (animFrame) return;

    actions[currentAction]();
    setMessage(`${currentAction} done!`);
    setTimeout(() => setMessage(""), 1000);
  };

  const handleLeft = () => {
    setMenuIndex((i) => (i - 1 + menuOptions.length) % menuOptions.length);
  };

  const handleRight = () => {
    setMenuIndex((i) => (i + 1) % menuOptions.length);
  };

  // --- Frame Selection Logic ---
  // Determines which ASCII art to show based on the current state.
  let displayFrame;
  if (!alive) {
    displayFrame = deadFrame;
  } else if (animFrame) {
    displayFrame = animFrame; // Temporary animations (play, eat) have priority.
  } else if (sleeping) {
    displayFrame = sleepFrames[frameIndex % sleepFrames.length];
  } else if (dirty) {
    displayFrame = dirtyFrame; // Show static dirty frame.
  } else {
    displayFrame = idleFrames[frameIndex % idleFrames.length];
  }

  return (
    <div className={`tamagotchi-shell ${attention ? "attention" : ""}`}>
      <div className="screen">
        <pre className="cat">{displayFrame}</pre>
        <div className="stats">
          H:{Math.round(hunger)} Ha:{Math.round(happiness)} Hl:
          {Math.round(health)} A:{age.toFixed(1)}
        </div>
        {message && <div className="message">{message}</div>}
        <div className="menu">[{menuOptions[menuIndex]}]</div>
      </div>

      <div className="buttons">
        <button className="btn left" onClick={handleLeft}></button>
        <button className="btn middle" onClick={handleConfirm}></button>
        <button className="btn right" onClick={handleRight}></button>
      </div>
    </div>
  );
}
